import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast, useRole, formatDate } from '../components/shared';
import { DeviceLogForm, RESULT, visitBadge, everyLabel } from '../components/PmLog';
import Photo from '../components/Photo';
import { DeviceRepair, VisitInvoices } from '../components/PmRepair';
import PdfReport from '../components/PdfReport';

export default function VisitPage() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [open, setOpen] = useState(null);
  const toast = useToast();
  const role = useRole();
  const canLog = role === 'admin' || role === 'technician';
  const navigate = useNavigate();

  const load = useCallback(() => api.pmVisit(id).then(setV).catch(e => toast(e.message, 'error')), [id, toast]);
  useEffect(() => { load(); }, [load]);
  if (!v) return <div className="page"><div className="empty">Loading...</div></div>;

  const logged = v.devices.filter(d => d.item).length;
  const [l, cls] = visitBadge(v);
  async function log(cd, body) {
    const r = await api.pmLogItem(v.id, { contract_device_id: cd.id, ...body });
    setV(r); setOpen(null); toast(`${cd.name} ✓`);
  }

  return (
    <div className="page" dir="rtl" style={{ maxWidth: 820 }}>
      <div className="flex items-center gap-8 mb-16" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/maintenance/${v.contract_id}`)}>→ العقد</button>
        <PdfReport kind="visit" id={v.id} />
      </div>
      <div className="card mb-16" style={{ padding: 16, borderTop: '4px solid var(--primary)' }}>
        <div className="flex items-center gap-8" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div className="flex items-center gap-8">
            <Photo kind="client" id={v.client_id} version={v.client_photo_at} size={52} icon="🏥" canEdit={canLog} onChange={load} />
            <div className="fw-600" style={{ fontSize: 20 }}>{v.client_name}</div>
          </div>
          <span className={`badge ${cls}`}>{l}</span>
        </div>
        <div className="text-sm text-muted mt-4">زيارة {v.seq} من {v.visits_total} · {formatDate(v.scheduled_date)} · {everyLabel(v.every_value, v.every_unit)}
          {v.client_phone && <> · <a className="link" href={`tel:${v.client_phone}`}>📞 {v.client_phone}</a></>}</div>
        <div style={{ height: 8, background: 'var(--bg3)', borderRadius: 4, marginTop: 12, overflow: 'hidden' }}>
          <div style={{ width: `${v.devices.length ? logged / v.devices.length * 100 : 0}%`, height: '100%', background: 'var(--green)', transition: 'width .3s' }} />
        </div>
        <div className="text-xs text-dim mt-4">{logged} من {v.devices.length} جهاز اتسجل</div>
      </div>

      <button className="btn btn-primary mb-16" style={{ width: '100%' }} onClick={() => fetch(`/api/field-visits/from-pm/${v.id}`, { method: 'POST' }).then(r => r.json()).then(r => r.id ? navigate(`/field-visits/${r.id}`) : toast(r.error || 'error', 'error'))}>
        💰 البنود والفاتورة والمصاريف لزيارة العقد دي
      </button>
      {role !== 'technician' && v.invoices.length > 0 && <VisitInvoices v={v} isAdmin={role === 'admin'} onChange={setV} />}

      {v.devices.map(cd => {
        const it = cd.item;
        const [rl, rcls] = it ? (RESULT[it.result] || [it.result, 'badge-gray']) : [];
        return (
          <div key={cd.id} className="card mb-8" style={{ padding: 14, borderInlineStart: `4px solid ${it ? (it.result === 'ok' ? 'var(--green)' : 'var(--yellow)') : 'var(--border2)'}` }}>
            <div className="flex items-center gap-8" style={{ flexWrap: 'wrap' }}>
              <Photo kind="device" id={cd.device_id} version={cd.photo_at} size={56} icon="📟" canEdit={canLog} onChange={load} />
              <div style={{ flex: 1, minWidth: 160 }}>
                <span className="fw-600">{cd.name}</span>{cd.alias && <span className="text-muted"> · {cd.alias}</span>}
                <div className="mono text-xs text-dim" dir="ltr" style={{ textAlign: 'right' }}>{cd.serial}</div>
              </div>
              {it && <span className={`badge ${rcls}`}>{rl}</span>}
              {canLog && open !== cd.id && <button className={`btn btn-sm ${it ? 'btn-ghost' : 'btn-primary'}`} onClick={() => setOpen(cd.id)}>{it ? 'تعديل' : '✓ سجّل'}</button>}
            </div>
            {open !== cd.id && (
              <div className="text-sm mt-8" style={{ color: 'var(--text2)' }}>
                {cd.due_tasks.map((t, i) => {
                  const done = it && (it.checklist.find(c => c.text === t.text) || {}).ok;
                  return <div key={i}>{it ? (done ? '✅' : '⬜') : '•'} {t.text}</div>;
                })}
                {it && <div className="text-xs text-dim mt-4">👤 {it.done_by} · {formatDate(it.done_at)}{it.notes ? ` · ${it.notes}` : ''}</div>}
              </div>
            )}
            {open === cd.id && <DeviceLogForm compact tasks={cd.due_tasks} initial={it} onSubmit={b => log(cd, b)} onCancel={() => setOpen(null)} />}
            <DeviceRepair visitId={v.id} cd={cd} canEdit={canLog} onChange={setV} onOpenTicket={tid => navigate(`/tickets/${tid}`)} />
          </div>
        );
      })}
    </div>
  );
}
